import React from 'react';
import { Link } from 'react-router-dom';
import { TrendingUp, BarChart3, PieChart, Target, Shield, Users, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import ContactForm from '../components/ContactForm';
import SEO from '../components/SEO';

const AssetManagement = () => {
  const features = [
    {
      icon: Target,
      title: 'Option Overlay Strategies',
      description: 'Advanced covered calls and protective puts to generate tax-efficient income while protecting your downside.',
    },
    {
      icon: BarChart3,
      title: 'Active Portfolio Management',
      description: 'Continuous monitoring and strategic adjustments to optimize performance in any market condition.',
    },
    {
      icon: PieChart,
      title: 'Custom Tailored Solutions',
      description: 'Every portfolio is uniquely designed around your specific goals, risk tolerance, and tax situation.',
    },
    {
      icon: Shield,
      title: 'Downside Protection',
      description: 'Strategic use of protective puts to limit losses during market downturns while maintaining upside potential.',
    },
    {
      icon: TrendingUp,
      title: 'Higher Risk-Adjusted Returns',
      description: 'Our option strategies are designed to enhance returns while reducing overall portfolio volatility.',
    },
    {
      icon: Users,
      title: 'Dedicated Advisory Team',
      description: 'Personal attention from licensed advisors who actively manage every aspect of your investments.',
    },
  ];

  const approach = [
    {
      step: '01',
      title: 'Discovery & Assessment',
      description: 'We begin by understanding your financial situation, goals, and risk tolerance through comprehensive consultation.',
    },
    {
      step: '02',
      title: 'Strategy Development',
      description: 'Our team crafts a personalized investment strategy designed to meet your unique objectives.',
    },
    {
      step: '03',
      title: 'Implementation',
      description: 'We execute your investment plan with precision, leveraging our extensive market expertise.',
    },
    {
      step: '04',
      title: 'Ongoing Management',
      description: 'Continuous monitoring, rebalancing, and optimization to ensure your portfolio stays on track.',
    },
  ];

  const schema = {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": "Wealth Management Services",
    "provider": {
      "@type": "FinancialService",
      "name": "Paracca Financial Group"
    },
    "areaServed": "nationwide in all 50 states",
    "description": "Professional wealth management and investment advisory services for individuals in nationwide in all 50 states."
  };

  return (
    <>
      <SEO 
        title="Wealth Management Services | Paracca Financial Group - Nationwide Service"
        description="Expert wealth management and wealth advisory services nationwide. Personalized investment strategies for individuals and families in all 50 states."
        keywords="wealth management sarasota fl, wealth management sarasota, investment advisor sarasota, portfolio management florida"
        schema={schema}
      />

      {/* Hero Section */}
      <section className="relative h-[400px] md:h-[500px] overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url('https://images.unsplash.com/photo-1454165804606-c3d57bc86b40')`,
          }}
        >
          <div className="absolute inset-0 bg-[#0a69b2] opacity-70"></div>
        </div>
        
        <div className="relative h-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center">
          <div className="max-w-3xl">
            <h1 className="font-bellefair text-4xl md:text-6xl  text-white mb-6 uppercase">
              Wealth Management
            </h1>
            <p className="font-montserrat text-lg md:text-xl text-gray-100 leading-relaxed">
              Strategic wealth management solutions tailored to your financial aspirations
            </p>
          </div>
        </div>
      </section>

      {/* Introduction */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="font-bellefair text-3xl md:text-4xl  text-[#0a69b2] mb-6 uppercase">
              Institutional Grade Investment Management Paired with Personalized Financial Planning
            </h2>
            <p className="font-montserrat text-lg text-gray-600 leading-relaxed mb-8">
              At Paracca Financial Group, we go beyond traditional portfolio management. Our wealth management approach uses proven strategies—including direct indexing and option overlays—to help you keep more of what you earn while building long-term wealth.
            </p>
            <p className="font-montserrat text-lg text-gray-600 leading-relaxed">
              We take the time to understand what matters most to you—your financial goals, your concerns, and your aspirations for the future. Every portfolio is actively managed and personalized based on your unique situation. No templates, no one-size-fits-all solutions. Just smart strategies designed to help you reach your goals with less risk and lower taxes.
            </p>
          </div>
        </div>
      </section>

      {/* Direct Indexing Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-5xl mx-auto">
            <h2 className="font-bellefair text-3xl md:text-4xl  text-[#0a69b2] mb-6 uppercase text-center">
              Direct Indexing: Own the Index, Not Just Track It
            </h2>
            <p className="font-montserrat text-lg text-gray-600 mb-8 leading-relaxed text-center">
              Most investors own index funds or ETFs—you get market exposure, but you're limited in what you can do. With direct indexing, you actually own the individual stocks that make up any major index—S&P 500, Russell 2000, NASDAQ, or even a custom combination tailored to your goals.
            </p>
            
            <div className="space-y-6">
              <div className="bg-white p-8 rounded-lg shadow-md">
                <h3 className="font-bellefair text-xl  text-[#0a69b2] mb-4 uppercase">
                  Why This Matters
                </h3>
                <p className="font-montserrat text-gray-600 leading-relaxed mb-4">
                  When you own the actual stocks instead of just an ETF, you unlock opportunities that traditional index investors simply don't have:
                </p>
                <ul className="space-y-3 font-montserrat text-gray-600">
                  <li className="flex items-start gap-2">
                    <span className="text-[#0a69b2] font-bold text-xl">•</span>
                    <span><strong>Tax Loss Harvesting:</strong> Sell losing positions to offset gains—even in up markets</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-[#0a69b2] font-bold text-xl">•</span>
                    <span><strong>Personalization:</strong> Exclude industries or companies that don't align with your values</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-[#0a69b2] font-bold text-xl">•</span>
                    <span><strong>Custom Combinations:</strong> Blend multiple indexes or create a portfolio uniquely suited to your situation</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-[#0a69b2] font-bold text-xl">•</span>
                    <span><strong>Lower Taxes:</strong> Harvest losses year-round to significantly reduce your tax bill</span>
                  </li>
                </ul>
              </div>

              <div className="bg-[#0a69b2] text-white p-8 rounded-lg shadow-md">
                <h3 className="font-bellefair text-2xl  mb-3 uppercase">
                  The Bottom Line
                </h3>
                <p className="font-montserrat text-lg leading-relaxed">
                  Same market exposure as traditional index funds, but with far more control, flexibility, and tax efficiency. Whether you want to track the S&P 500, Russell 2000, NASDAQ, or build a custom blend, direct indexing gives you the tools large institutions use—applied to your personal portfolio.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Option Overlay Strategy Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="font-bellefair text-3xl md:text-4xl  text-[#0a69b2] mb-6 uppercase">
                Generate Income & Protect Against Losses
              </h2>
              <p className="font-montserrat text-lg text-gray-600 mb-6 leading-relaxed">
                Beyond direct indexing, we use proven option strategies to help you earn extra income and protect your portfolio during market downturns.
              </p>
              
              <div className="space-y-6">
                <div className="bg-white p-6 rounded-lg shadow-md">
                  <h3 className="font-bellefair text-xl  text-[#0a69b2] mb-3 uppercase">
                    Covered Calls: Generate Extra Income
                  </h3>
                  <p className="font-montserrat text-gray-600 leading-relaxed">
                    Think of this as renting out your stocks. You keep ownership and any dividends, but you also collect regular premium payments. This creates steady cash flow on top of your investment gains—money you can reinvest or use for living expenses.
                  </p>
                </div>

                <div className="bg-white p-6 rounded-lg shadow-md">
                  <h3 className="font-bellefair text-xl  text-[#0a69b2] mb-3 uppercase">
                    Protective Puts: Insurance for Your Portfolio
                  </h3>
                  <p className="font-montserrat text-gray-600 leading-relaxed">
                    Just like insurance on your home or car, protective puts limit your losses if the market drops. You still participate in market gains, but you have a safety net that kicks in during downturns—helping you sleep better at night.
                  </p>
                </div>
              </div>
            </div>

            <div className="relative">
              <img 
                src="https://images.unsplash.com/photo-1611974789855-9c2a0a7236a3"
                alt="Investment Management Portfolio Strategies"
                className="rounded-lg shadow-2xl"
              />
              <div className="absolute -bottom-6 -left-6 bg-[#0a69b2] text-white p-6 rounded-lg shadow-xl max-w-xs">
                <p className="font-bellefair text-3xl  mb-2">Active</p>
                <p className="font-montserrat text-sm">Real people managing your money, not algorithms</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Direct Indexing Tax Loss Harvesting */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="font-bellefair text-3xl md:text-4xl text-[#0a69b2] mb-6 uppercase">
                Direct Indexing Tax Loss Harvesting
              </h2>
              <p className="font-montserrat text-lg text-gray-600 leading-relaxed max-w-3xl mx-auto">
                Lower your taxes every year—automatically and consistently
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-8 mb-12">
              <div className="bg-white rounded-2xl p-8 shadow-lg border-2 border-gray-200">
                <h3 className="font-bellefair text-xl text-[#0a69b2] mb-4 uppercase">
                  Traditional Index Funds
                </h3>
                <p className="font-montserrat text-gray-700 leading-relaxed mb-4">
                  With a regular S&P 500 ETF or mutual fund, you own shares of the fund itself—not the individual stocks. You can only harvest losses if the entire fund goes down in value.
                </p>
                <div className="bg-red-50 border-l-4 border-red-400 p-4">
                  <p className="font-montserrat text-sm text-gray-700">
                    <strong>The Problem:</strong> No losses to harvest when the market goes up. You miss out on thousands in potential tax savings every year.
                  </p>
                </div>
              </div>

              <div className="bg-white rounded-2xl p-8 shadow-lg border-2 border-[#0a69b2]">
                <h3 className="font-bellefair text-xl text-[#0a69b2] mb-4 uppercase">
                  Direct Indexing Approach
                </h3>
                <p className="font-montserrat text-gray-700 leading-relaxed mb-4">
                  Because you own all 500 individual stocks, some will be up and some will be down—even when the overall market is rising. We harvest losses from the underperformers while maintaining your market exposure.
                </p>
                <div className="bg-green-50 border-l-4 border-green-500 p-4">
                  <p className="font-montserrat text-sm text-gray-700">
                    <strong>The Advantage:</strong> Harvest losses year-round, regardless of market direction. More tax savings, more wealth kept in your pocket.
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-2xl p-8 md:p-12 shadow-lg border-2 border-[#0a69b2]">
              <h3 className="font-bellefair text-2xl text-[#0a69b2] mb-8 uppercase text-center">
                How It Works in Practice
              </h3>
              
              <div className="space-y-8">
                <div className="flex items-start gap-6">
                  <div className="flex-shrink-0">
                    <div className="w-12 h-12 bg-[#0a69b2] text-white rounded-full flex items-center justify-center font-montserrat font-bold text-lg">
                      1
                    </div>
                  </div>
                  <div className="flex-1">
                    <h4 className="font-bellefair text-lg text-gray-900 mb-2 uppercase">Continuous Monitoring</h4>
                    <p className="font-montserrat text-gray-700 leading-relaxed">
                      Throughout the year, we monitor all 500 positions in your portfolio. As individual stocks fluctuate, we identify opportunities to harvest losses from positions that are down—even if the overall S&P 500 index is up for the year.
                    </p>
                  </div>
                </div>

                <div className="border-t border-gray-200 pt-8">
                  <div className="flex items-start gap-6">
                    <div className="flex-shrink-0">
                      <div className="w-12 h-12 bg-[#0a69b2] text-white rounded-full flex items-center justify-center font-montserrat font-bold text-lg">
                        2
                      </div>
                    </div>
                    <div className="flex-1">
                      <h4 className="font-bellefair text-lg text-gray-900 mb-2 uppercase">Strategic Selling & Replacing</h4>
                      <p className="font-montserrat text-gray-700 leading-relaxed mb-3">
                        When we find a stock trading below your purchase price, we sell it to realize the tax loss. Then we immediately buy a similar stock (for example, selling Microsoft to buy Oracle) to maintain your market exposure. You stay fully invested while capturing tax benefits.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="border-t border-gray-200 pt-8">
                  <div className="flex items-start gap-6">
                    <div className="flex-shrink-0">
                      <div className="w-12 h-12 bg-[#0a69b2] text-white rounded-full flex items-center justify-center font-montserrat font-bold text-lg">
                        3
                      </div>
                    </div>
                    <div className="flex-1">
                      <h4 className="font-bellefair text-lg text-gray-900 mb-2 uppercase">Year-Round Tax Savings</h4>
                      <p className="font-montserrat text-gray-700 leading-relaxed mb-3">
                        These harvested losses offset your capital gains (from stocks, real estate, or business sales) and can even reduce your ordinary income by up to $3,000 per year. Unused losses carry forward indefinitely.
                      </p>
                      <div className="bg-blue-50 border-l-4 border-[#0a69b2] p-4 mt-4">
                        <p className="font-montserrat text-sm text-gray-700">
                          <strong>Real Impact:</strong> Many clients save $10,000-$50,000+ per year in taxes compared to holding a traditional index fund. Over 10-20 years, that's hundreds of thousands more in your portfolio.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="font-bellefair text-3xl md:text-4xl  text-[#0a69b2] mb-4 uppercase">
              Our Investment Management Approach
            </h2>
            <p className="font-montserrat text-lg text-gray-600">
              Comprehensive, actively managed solutions customized for your success
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature) => {
              const IconComponent = feature.icon;
              return (
                <Card key={feature.title} className="hover:shadow-xl transition-shadow duration-300 border-2">
                  <CardContent className="p-6">
                    <div className="bg-[#0a69b2] w-14 h-14 rounded-lg flex items-center justify-center mb-4">
                      <IconComponent className="text-white" size={28} />
                    </div>
                    <h3 className="font-bellefair text-xl  text-[#0a69b2] mb-3 uppercase">
                      {feature.title}
                    </h3>
                    <p className="font-montserrat text-gray-600 leading-relaxed">
                      {feature.description}
                    </p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Our Approach */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="font-bellefair text-3xl md:text-4xl  text-[#0a69b2] mb-4 uppercase">
              The Benefits of Our Strategy
            </h2>
            <p className="font-montserrat text-lg text-gray-600 max-w-3xl mx-auto">
              How our active, custom-tailored approach delivers superior outcomes
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
            <div className="bg-white p-8 rounded-lg shadow-md hover:shadow-xl transition-shadow duration-300">
              <div className="bg-[#0a69b2] w-14 h-14 rounded-lg flex items-center justify-center mb-4 mx-auto">
                <TrendingUp className="text-white" size={28} />
              </div>
              <h3 className="font-bellefair text-xl  text-[#0a69b2] mb-3 text-center uppercase">
                Higher Risk-Adjusted Returns
              </h3>
              <p className="font-montserrat text-gray-600 text-center leading-relaxed">
                Our option overlay strategies are designed to enhance returns while simultaneously reducing portfolio volatility—achieving better outcomes per unit of risk taken.
              </p>
            </div>

            <div className="bg-white p-8 rounded-lg shadow-md hover:shadow-xl transition-shadow duration-300">
              <div className="bg-[#0a69b2] w-14 h-14 rounded-lg flex items-center justify-center mb-4 mx-auto">
                <Shield className="text-white" size={28} />
              </div>
              <h3 className="font-bellefair text-xl  text-[#0a69b2] mb-3 text-center uppercase">
                Downside Protection
              </h3>
              <p className="font-montserrat text-gray-600 text-center leading-relaxed">
                Protective puts limit your losses during market downturns while maintaining upside participation—protecting your wealth when it matters most.
              </p>
            </div>

            <div className="bg-white p-8 rounded-lg shadow-md hover:shadow-xl transition-shadow duration-300">
              <div className="bg-[#0a69b2] w-14 h-14 rounded-lg flex items-center justify-center mb-4 mx-auto">
                <Target className="text-white" size={28} />
              </div>
              <h3 className="font-bellefair text-xl  text-[#0a69b2] mb-3 text-center uppercase">
                Tax-Efficient Income
              </h3>
              <p className="font-montserrat text-gray-600 text-center leading-relaxed">
                Covered calls generate consistent income that can be structured for optimal tax treatment—creating cash flow without triggering unnecessary capital gains.
              </p>
            </div>
          </div>

          <div className="bg-white p-8 rounded-lg shadow-lg">
            <h3 className="font-bellefair text-2xl  text-[#0a69b2] mb-4 text-center uppercase">
              Truly Custom, Active Management
            </h3>
            <p className="font-montserrat text-lg text-gray-600 text-center leading-relaxed max-w-4xl mx-auto">
              Unlike passive strategies or robo-advisors, every portfolio we manage receives personalized attention from our licensed advisors. We actively adjust your option overlays based on market conditions, your tax situation, income needs, and evolving financial goals. This hands-on approach ensures your investments are always aligned with your best interests—and why our clients experience superior long-term outcomes.
            </p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-[#0a69b2] text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-bellefair text-3xl md:text-4xl  mb-6 uppercase">
            Start Building Your Wealth Strategy Today
          </h2>
          <p className="font-montserrat text-lg mb-8 leading-relaxed text-gray-100">
            Schedule a complimentary consultation to discuss your investment goals
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              size="lg"
              className="bg-white text-[#0a69b2] hover:bg-gray-100 font-montserrat font-semibold px-8 py-6 transition-all duration-300"
              asChild
            >
              <a href="tel:941-545-9057">
                Call 941-545-9057
              </a>
            </Button>
            <Button 
              size="lg"
              variant="outline"
              className="bg-transparent border-2 border-white text-white hover:bg-white hover:text-[#0a69b2] font-montserrat font-semibold px-8 py-6"
              asChild
            >
              <Link to="/">
                Learn More
                <ArrowRight className="ml-2" size={20} />
              </Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Contact Form */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="font-bellefair text-3xl md:text-4xl  text-[#0a69b2] mb-4 uppercase">
              Request a Consultation
            </h2>
            <p className="font-montserrat text-lg text-gray-600">
              Let's discuss how we can help you achieve your financial goals
            </p>
          </div>
          <ContactForm />
        </div>
      </section>
    </>
  );
};

export default AssetManagement;
